import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from '../../components/Pagination';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface Customer {
  customer_id: string;
  company_name: string;
  contact_email: string;
  status: string;
  license_status: string | null;
  license_expires: string | null;
  max_nodes: number | null;
  ha_enabled: boolean | null;
}

interface GeneratedLicense {
  license_id: number;
  license_key: string;
  expires_at: string;
  customer_name?: string;
}

const getDaysUntilExpiry = (expiresAt: string | null): number | null => {
  if (!expiresAt) return null;
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffTime = expiry.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export default function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showLicenseResultModal, setShowLicenseResultModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
  const [generatedLicense, setGeneratedLicense] = useState<GeneratedLicense | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [licenseFilter, setLicenseFilter] = useState<'all' | 'with_license' | 'no_license'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, licenseFilter, statusFilter]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCustomer = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      await axios.post(`${API_URL}/admin/customers`, {
        company_name: formData.get('company_name'),
        contact_email: formData.get('contact_email'),
        contact_name: formData.get('contact_name'),
        password: formData.get('password'),
        notes: formData.get('notes'),
      });

      setShowCreateModal(false);
      fetchCustomers();
      alert('Customer created successfully!');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  const generateLicense = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await axios.post(`${API_URL}/admin/generate-license`, {
        customer_id: selectedCustomer,
        expiration_days: parseInt(formData.get('expiration_days') as string),
        features: {
          max_nodes: parseInt(formData.get('max_nodes') as string),
          ha_enabled: formData.get('ha_enabled') === 'on',
        }
      });

      setGeneratedLicense({
        ...response.data,
        customer_name: selectedCustomerName
      });
      setShowLicenseModal(false);
      setShowLicenseResultModal(true);
      fetchCustomers();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to generate license');
    }
  };

  const viewExistingLicense = async (customerId: string, customerName: string) => {
    try {
      const response = await axios.get(`${API_URL}/admin/license/${customerId}`);
      setGeneratedLicense({
        license_id: response.data.license_id,
        license_key: response.data.license_key,
        expires_at: response.data.expires_at,
        customer_name: response.data.customer_name
      });
      setSelectedCustomer(customerId);
      setShowLicenseResultModal(true);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to fetch license');
    }
  };

  const copyLicenseToClipboard = () => {
    if (generatedLicense) {
      navigator.clipboard.writeText(generatedLicense.license_key);
      alert('License key copied to clipboard!');
    }
  };

  const downloadLicenseAsEnv = () => {
    if (generatedLicense) {
      const content = `# MoonshotIoT License\n# Customer: ${generatedLicense.customer_name}\n# Expires: ${new Date(generatedLicense.expires_at).toLocaleDateString()}\n\nLICENSE_KEY=${generatedLicense.license_key}\n`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `license-${selectedCustomer}.env`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const getFilteredCustomers = () => {
    let filtered = customers;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.company_name.toLowerCase().includes(term) ||
        c.contact_email.toLowerCase().includes(term) ||
        c.customer_id.toLowerCase().includes(term)
      );
    }

    // License filter
    if (licenseFilter === 'with_license') {
      filtered = filtered.filter(c => c.license_status !== null);
    } else if (licenseFilter === 'no_license') {
      filtered = filtered.filter(c => c.license_status === null);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    return filtered;
  };

  const filteredCustomers = getFilteredCustomers();
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getExpiryBadge = (daysLeft: number | null) => {
    if (daysLeft === null) return null;
    if (daysLeft < 0) return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">Expired</span>;
    if (daysLeft <= 30) return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">{daysLeft}d</span>;
    if (daysLeft <= 60) return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-800">{daysLeft}d</span>;
    if (daysLeft <= 90) return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">{daysLeft}d</span>;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600">Manage customers and their licenses</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Customer
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search by company, customer ID, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">License:</span>
          <select
            value={licenseFilter}
            onChange={(e) => setLicenseFilter(e.target.value as 'all' | 'with_license' | 'no_license')}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="with_license">With License</option>
            <option value="no_license">No License</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <span className="text-sm text-gray-400">
          {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">License</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Features</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  {searchTerm || licenseFilter !== 'all' || statusFilter !== 'all'
                    ? 'No customers match your criteria.'
                    : 'No customers yet. Create your first customer to get started.'}
                </td>
              </tr>
            ) : (
              paginatedCustomers.map(customer => {
                const daysLeft = getDaysUntilExpiry(customer.license_expires);
                return (
                  <tr key={customer.customer_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{customer.company_name}</div>
                      <div className="text-sm text-gray-500">{customer.customer_id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{customer.contact_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      {customer.license_status ? (
                        <span className={`px-2 py-1 text-xs rounded ${
                          customer.license_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {customer.license_status}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">No license</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {customer.max_nodes !== null ? (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-600">{customer.max_nodes} nodes</span>
                          {customer.ha_enabled && (
                            <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800">HA</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {customer.license_expires ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900">
                            {new Date(customer.license_expires).toLocaleDateString()}
                          </span>
                          {getExpiryBadge(daysLeft)}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      {customer.license_status && (
                        <button
                          onClick={() => viewExistingLicense(customer.customer_id, customer.company_name)}
                          className="text-sm text-green-600 hover:underline"
                        >
                          View Key
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedCustomer(customer.customer_id);
                          setSelectedCustomerName(customer.company_name);
                          setShowLicenseModal(true);
                        }}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {customer.license_status ? 'Renew' : 'Generate'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalItems={filteredCustomers.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      {/* Create Customer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create Customer</h3>
            <form onSubmit={createCustomer}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Company Name *</label>
                  <input name="company_name" required className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Name *</label>
                  <input name="contact_name" required className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input name="contact_email" type="email" required className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Initial Password *</label>
                  <input name="password" type="password" required className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea name="notes" rows={2} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">Create</button>
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 bg-gray-200 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate License Modal */}
      {showLicenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Generate License for {selectedCustomerName}</h3>
            <form onSubmit={generateLicense}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Expiration Days</label>
                  <input name="expiration_days" type="number" defaultValue={365} min={1} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Nodes</label>
                  <input name="max_nodes" type="number" defaultValue={100} min={1} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="flex items-center gap-2">
                    <input name="ha_enabled" type="checkbox" defaultChecked className="rounded" />
                    <span className="text-sm font-medium">High Availability Enabled</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700">Generate</button>
                <button type="button" onClick={() => setShowLicenseModal(false)} className="flex-1 bg-gray-200 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* License Result Modal */}
      {showLicenseResultModal && generatedLicense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-green-700">License Key</h3>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm text-gray-500">Customer</span>
                  <p className="font-medium">{generatedLicense.customer_name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Expires</span>
                  <p className="font-medium">{new Date(generatedLicense.expires_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div>
                <span className="text-sm text-gray-500">License Key</span>
                <div className="mt-1 p-3 bg-gray-900 rounded font-mono text-xs text-green-400 break-all max-h-32 overflow-y-auto">
                  {generatedLicense.license_key}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={copyLicenseToClipboard} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy
              </button>
              <button onClick={downloadLicenseAsEnv} className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download .env
              </button>
              <button onClick={() => { setShowLicenseResultModal(false); setGeneratedLicense(null); }} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
